import React, { useEffect, useMemo, useState } from "react";
import { Save, Loader2, AlertTriangle, RotateCcw, Languages, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { readError } from "@/lib/api";

const LOCALES = ["hr", "en", "de", "fr"];
const LABEL = { hr: "HR", en: "EN", de: "DE", fr: "FR" };

/**
 * Table-style translation editor.
 * Rows are keyed by `<namespace>::<dotted.path>`; columns are HR/EN/DE/FR.
 * After Save you can hit "Apply" to restart the frontend supervisor process.
 */
export default function AdminTranslations() {
  const [rows, setRows] = useState(null);
  const [original, setOriginal] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/translations");
      setRows(data.rows || []);
      // snapshot for diff
      const snap = {};
      (data.rows || []).forEach((r) => { snap[r.key] = { ...r.values }; });
      setOriginal(snap);
    } catch (err) {
      setError(readError(err, "Could not load translations."));
    }
  };

  useEffect(() => { load(); }, []);

  const changedRows = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => {
      const o = original[r.key] || {};
      return LOCALES.some((l) => (r.values[l] ?? "") !== (o[l] ?? ""));
    });
  }, [rows, original]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter((r) =>
      r.key.toLowerCase().includes(f) ||
      LOCALES.some((l) => (r.values[l] || "").toLowerCase().includes(f))
    );
  }, [rows, filter]);

  const updateCell = (key, locale, value) => {
    setRows((rs) => rs.map((r) => r.key === key ? { ...r, values: { ...r.values, [locale]: value } } : r));
  };

  const save = async (apply = false) => {
    if (apply) setApplying(true); else setBusy(true);
    try {
      await api.put("/admin/translations", { rows: changedRows }, { params: { apply } });
      toast.success(apply ? "Translations saved — frontend is restarting…" : "Translations saved.");
      // refresh snapshot
      const snap = {};
      rows.forEach((r) => { snap[r.key] = { ...r.values }; });
      setOriginal(snap);
    } catch (err) {
      toast.error(readError(err, "Save failed."));
    } finally {
      setBusy(false);
      setApplying(false);
    }
  };

  const revert = () => {
    setRows((rs) => rs.map((r) => ({ ...r, values: { ...(original[r.key] || {}) } })));
    toast.success("Reverted local changes.");
  };

  return (
    <div className="space-y-7 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Translations</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Nav labels, button text, footer strings. Edit one row across all four locales side-by-side.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {changedRows.length > 0 && (
            <Button variant="outline" onClick={revert} disabled={busy || applying} data-testid="translations-revert">
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} /> Revert
            </Button>
          )}
          <Button onClick={() => save(false)} disabled={busy || applying || changedRows.length === 0} data-testid="translations-save" className="shadow-interactive">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
            Save {changedRows.length > 0 ? `(${changedRows.length})` : ""}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={applying || changedRows.length === 0} variant="secondary" data-testid="translations-apply">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" strokeWidth={1.5} />}
                Apply & restart
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Restart the frontend?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will save changes <strong>and</strong> restart the frontend service. The public site may be unavailable for ~5 seconds while it boots.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="translations-apply-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => save(true)} data-testid="translations-apply-confirm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Yes, save and restart
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      <div className="rounded-lg border border-border bg-card/60 p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by key or text…"
          className="h-9 border-0 bg-transparent shadow-none focus-visible:ring-0"
          data-testid="translations-filter"
        />
        <div className="text-xs text-muted-foreground whitespace-nowrap pr-2">{filtered.length} / {rows?.length || 0}</div>
      </div>

      <Card className="bg-card border-border/60 shadow-ambient overflow-hidden">
        <CardContent className="p-0">
          {rows === null ? (
            <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading translations…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="translations-table">
                <thead>
                  <tr className="bg-muted/40 border-b border-border/60">
                    <th className="text-left font-medium px-4 py-3 w-[22%]">Key</th>
                    {LOCALES.map((l) => (
                      <th key={l} className="text-left font-medium px-4 py-3">{LABEL[l]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const isChanged = LOCALES.some((l) => (row.values[l] ?? "") !== ((original[row.key] || {})[l] ?? ""));
                    return (
                      <tr key={row.key} data-testid={`translation-row-${row.key}`} className={`border-b border-border/40 hover:bg-muted/20 ${isChanged ? "bg-primary/5" : ""}`}>
                        <td className="px-4 py-2.5 align-top font-mono text-xs text-foreground/80 break-all">{row.key}</td>
                        {LOCALES.map((l) => {
                          const value = row.values[l] || "";
                          const isLong = value.length > 60;
                          return (
                            <td key={l} className="px-2 py-2 align-top min-w-[160px]">
                              {isLong ? (
                                <textarea
                                  value={value}
                                  onChange={(e) => updateCell(row.key, l, e.target.value)}
                                  className="w-full min-h-[64px] resize-y rounded-md border border-border bg-background px-2.5 py-1.5 text-xs leading-snug focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  data-testid={`tr-${row.key}-${l}`}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => updateCell(row.key, l, e.target.value)}
                                  className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  data-testid={`tr-${row.key}-${l}`}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
